import React, { useState, useEffect, useCallback } from 'react';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import ConfirmDialog from '../../common/ConfirmDialog';
import { StatusBadge, EntityChip } from '../../ds';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Collapse,
  Tooltip,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Download as ExportIcon,
  FilterList as FilterIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  getPhysicalCounts,
  getPhysicalCountItems,
  deletePhysicalCountsBulk,
  deletePhysicalCountItem,
  updatePhysicalCountItem,
  approveAdjustments,
  exportPhysicalCounts,
  getWarehouses,
  formatDate,
  formatNumber
} from '../../../services/inventoryService';
import DeleteCountsDialog from './DeleteCountsDialog';

/**
 * PhysicalCountHistory - Tab 1: View and manage historical physical counts
 * Features: Filter, expand to see items, select and delete counts
 */
function PhysicalCountHistory({ companyId, brandId, onCountDeleted }) {
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [counts, setCounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [warehouses, setWarehouses] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    warehouse_code: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Expansion
  const [expandedId, setExpandedId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-item editing state
  // For multi-batch: { countId, sku, batches: [{ batch_number, counted_quantity }] }
  // For single-batch: { countId, sku, counted_quantity }
  const [editingItem, setEditingItem] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  // Load warehouses for filter dropdown
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const res = await getWarehouses({ is_active: true });
        setWarehouses(res?.warehouses || []);
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    };
    loadWarehouses();
  }, []);

  // Load counts
  const loadCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        company_id: companyId,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      // Add filters
      if (filters.warehouse_code) params.warehouse_code = filters.warehouse_code;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (brandId) params.brand_id = brandId;

      const res = await getPhysicalCounts(params);
      setCounts(res?.data || res?.counts || []);
      setTotal(res?.total || res?.data?.length || 0);
    } catch (err) {
      console.error('Failed to load physical counts:', err);
      setError('Failed to load physical counts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [companyId, brandId, page, rowsPerPage, filters]);

  useEffect(() => {
    if (companyId) {
      loadCounts();
    }
  }, [loadCounts, companyId]);

  // Load items for expanded count
  const loadCountItems = async (countId) => {
    if (expandedItems[countId]) return; // Already loaded

    setLoadingItems(true);
    try {
      const res = await getPhysicalCountItems(countId, companyId);
      setExpandedItems(prev => ({
        ...prev,
        [countId]: res?.data?.items || res?.items || []
      }));
    } catch (err) {
      console.error('Failed to load count items:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleExpandClick = (countId) => {
    if (expandedId === countId) {
      setExpandedId(null);
    } else {
      setExpandedId(countId);
      loadCountItems(countId);
    }
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(counts.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const isSelected = (id) => selectedIds.includes(id);
  const isAllSelected = counts.length > 0 && selectedIds.length === counts.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < counts.length;

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      warehouse_code: '',
      status: '',
      date_from: '',
      date_to: ''
    });
    setPage(0);
  };

  // Delete handlers
  const handleDeleteClick = () => {
    setError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (countIds) => {
    setDeleting(true);
    setError(null);
    try {
      await deletePhysicalCountsBulk({
        count_ids: countIds,
        company_id: companyId
      });

      setSuccess(`Successfully deleted ${countIds.length} physical count(s) and related records.`);
      setDeleteDialogOpen(false);
      setSelectedIds([]);
      loadCounts();

      // Notify parent
      if (onCountDeleted) {
        onCountDeleted();
      }
    } catch (err) {
      console.error('Failed to delete counts:', err);
      setError(err.response?.data?.error || 'Failed to delete physical counts. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Export handler
  const handleExport = () => {
    exportPhysicalCounts(filters);
  };

  // Per-item handlers
  const handleEditItem = (countId, item) => {
    const hasBatches = item.batches && item.batches.length > 1;

    if (hasBatches) {
      // Multi-batch: initialize with all batch quantities
      setEditingItem({
        countId,
        sku: item.sku,
        batches: item.batches.map(b => ({
          batch_number: b.batch_number,
          counted_quantity: b.counted_quantity || 0
        }))
      });
    } else {
      // Single batch
      setEditingItem({
        countId,
        sku: item.sku,
        counted_quantity: item.counted_quantity || 0
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  // Update a specific batch quantity while editing
  const handleBatchQuantityChange = (batchNumber, value) => {
    setEditingItem(prev => ({
      ...prev,
      batches: prev.batches.map(b =>
        b.batch_number === batchNumber
          ? { ...b, counted_quantity: parseFloat(value) || 0 }
          : b
      )
    }));
  };

  // Get total from batch quantities
  const getEditingTotal = () => {
    if (!editingItem?.batches) return editingItem?.counted_quantity || 0;
    return editingItem.batches.reduce((sum, b) => sum + (b.counted_quantity || 0), 0);
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;

    setSavingItem(true);
    setError(null);
    try {
      const updates = editingItem.batches
        ? { batch_updates: editingItem.batches }
        : { counted_quantity: editingItem.counted_quantity };

      await updatePhysicalCountItem(
        editingItem.countId,
        editingItem.sku,
        updates,
        companyId
      );

      // Refresh items for this count
      setExpandedItems(prev => ({ ...prev, [editingItem.countId]: null }));
      await loadCountItems(editingItem.countId);

      setSuccess(`Updated ${editingItem.sku} successfully`);
      setEditingItem(null);

      // Reload counts to get updated variance totals
      loadCounts();
    } catch (err) {
      console.error('Failed to update item:', err);
      setError(err.response?.data?.error || 'Failed to update item. Please try again.');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (countId, sku) => {
    if (!await confirm({ title: 'Delete SKU', message: `Delete SKU ${sku} from this count?`, confirmText: 'Delete', severity: 'danger' })) return;

    setError(null);
    try {
      await deletePhysicalCountItem(countId, sku, companyId);

      // Refresh items for this count
      setExpandedItems(prev => ({ ...prev, [countId]: null }));
      await loadCountItems(countId);

      setSuccess(`Deleted ${sku} from count`);

      // Reload counts to get updated totals
      loadCounts();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError(err.response?.data?.error || 'Failed to delete item. Please try again.');
    }
  };

  // Approve a pending count — applies it (creates the trust-point adjustment) and
  // updates stock. Counts are not auto-applied; this is the explicit review step.
  const handleApprove = async (countId) => {
    if (!await confirm({
      title: 'Approve count',
      message: 'Approve this count? It will be applied as the trust point and update stock levels for this SKU/warehouse.',
      confirmText: 'Approve',
      severity: 'warning'
    })) return;

    setApprovingId(countId);
    setError(null);
    try {
      await approveAdjustments(countId);
      setSuccess('Count approved — stock updated.');
      loadCounts();
    } catch (err) {
      console.error('Failed to approve count:', err);
      setError(err.response?.data?.error || 'Failed to approve count. Please try again.');
    } finally {
      setApprovingId(null);
    }
  };

  // Get selected counts for delete dialog
  const selectedCounts = counts.filter(c => selectedIds.includes(c.id));

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'draft': return 'default';
      case 'approved': return 'info';
      default: return 'default';
    }
  };

  // Get variance color
  const getVarianceColor = (variance) => {
    if (variance === 0) return 'success.main';
    if (Math.abs(variance) <= 10) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Toolbar */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Selection actions */}
            {selectedIds.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="primary">
                  {selectedIds.length} selected
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  Delete Selected
                </Button>
              </Box>
            )}

            {/* Filter toggle */}
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>

            <Box sx={{ flex: 1 }} />

            {/* Export */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExportIcon />}
              onClick={handleExport}
            >
              Export CSV
            </Button>

            {/* Refresh — button wrapped in <span> so the Tooltip has a
                non-disabled event target (MUI: a disabled button doesn't fire
                the events Tooltip listens for). */}
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadCounts} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Filter row */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Warehouse</InputLabel>
                    <Select
                      value={filters.warehouse_code}
                      label="Warehouse"
                      onChange={(e) => handleFilterChange('warehouse_code', e.target.value)}
                    >
                      <MenuItem value="">All Warehouses</MenuItem>
                      {warehouses.map(w => (
                        <MenuItem key={w.id} value={w.code}>{w.name} ({w.code})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Counts table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell width={40}></TableCell>
                <TableCell>Count #</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Count Date</TableCell>
                <TableCell align="right">Items</TableCell>
                <TableCell align="right">Variance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell width={60} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && counts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : counts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No physical counts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                counts.map((count) => (
                  <React.Fragment key={count.id}>
                    {/* Main row */}
                    <TableRow
                      hover
                      selected={isSelected(count.id)}
                      sx={{ '& > *': { borderBottom: expandedId === count.id ? 0 : undefined } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected(count.id)}
                          onChange={() => handleSelectOne(count.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandClick(count.id)}
                        >
                          {expandedId === count.id ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {count.count_number || `PC-${count.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <EntityChip code={count.warehouse_code} name={warehouses.find(w => w.code === count.warehouse_code)?.name} />
                      </TableCell>
                      <TableCell>{formatDate(count.count_date)}</TableCell>
                      <TableCell align="right">{formatNumber(count.total_items || 0)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={getVarianceColor(count.total_variance || 0)}
                          fontWeight="medium"
                        >
                          {(count.total_variance || 0) > 0 ? '+' : ''}{formatNumber(count.total_variance || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={count.status || 'completed'} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete this count">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedIds([count.id]);
                              setError(null);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expanded row */}
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse in={expandedId === count.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            {loadingItems && !expandedItems[count.id] ? (
                              <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle2">
                                    Count Items ({expandedItems[count.id]?.length || 0})
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {['pending_review', 'pending', 'draft'].includes(count.status) && (
                                      <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={() => handleApprove(count.id)}
                                        disabled={approvingId === count.id}
                                      >
                                        {approvingId === count.id ? 'Approving…' : 'Approve'}
                                      </Button>
                                    )}
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => {
                                        setSelectedIds([count.id]);
                                        setError(null);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      Delete This Count
                                    </Button>
                                  </Box>
                                </Box>

                                {expandedItems[count.id]?.length > 0 ? (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>SKU</TableCell>
                                        <TableCell align="right">System Qty</TableCell>
                                        <TableCell align="right">Counted Qty</TableCell>
                                        <TableCell align="right">Variance</TableCell>
                                        <TableCell align="center">Batches</TableCell>
                                        <TableCell>Reason</TableCell>
                                        <TableCell align="center" width={100}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {expandedItems[count.id].map((item, idx) => {
                                        const variance = item.variance !== undefined
                                          ? item.variance
                                          : (item.counted_quantity || 0) - (item.system_quantity || 0);
                                        const hasBatches = item.batches && item.batches.length > 0;
                                        const hasMultipleBatches = item.batches && item.batches.length > 1;
                                        const isEditing = editingItem?.countId === count.id && editingItem?.sku === item.sku;
                                        const isEditingMultiBatch = isEditing && editingItem?.batches;

                                        // Calculate editing variance for multi-batch
                                        const editingCountedQty = isEditingMultiBatch ? getEditingTotal() : (editingItem?.counted_quantity || 0);
                                        const editingVariance = isEditing ? (editingCountedQty - (item.system_quantity || 0)) : variance;

                                        return (
                                          <React.Fragment key={idx}>
                                            <TableRow>
                                              <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {item.sku}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">{formatNumber(item.system_quantity || 0)}</TableCell>
                                              <TableCell align="right">
                                                {isEditing ? (
                                                  isEditingMultiBatch ? (
                                                    // Multi-batch: show calculated total (non-editable at SKU level)
                                                    <Typography variant="body2" fontWeight="medium" color="primary">
                                                      {formatNumber(getEditingTotal())}
                                                    </Typography>
                                                  ) : (
                                                    // Single batch: editable here
                                                    <TextField
                                                      size="small"
                                                      type="number"
                                                      value={editingItem.counted_quantity}
                                                      onChange={(e) => setEditingItem(prev => ({
                                                        ...prev,
                                                        counted_quantity: parseFloat(e.target.value) || 0
                                                      }))}
                                                      sx={{ width: 100 }}
                                                      autoFocus
                                                    />
                                                  )
                                                ) : (
                                                  formatNumber(item.counted_quantity || item.counted_qty || 0)
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  color={editingVariance === 0 ? 'success.main' : 'error.main'}
                                                  fontWeight="medium"
                                                >
                                                  {editingVariance > 0 ? '+' : ''}{formatNumber(editingVariance)}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                {hasBatches ? (
                                                  <Tooltip title={item.batches.map(b => `${b.batch_number}: ${b.counted_quantity}`).join(', ')}>
                                                    <Chip
                                                      label={item.batch_count || item.batches.length}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  </Tooltip>
                                                ) : (
                                                  <Typography variant="body2" color="textSecondary">1</Typography>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                                                  {item.variance_reason || '—'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                {isEditing ? (
                                                  // Distinct key vs the view-actions Box below so React fully
                                                  // unmounts/remounts on the edit<->view toggle instead of reusing
                                                  // the same Tooltip with a swapped anchor child (<span> <-> IconButton).
                                                  // Reuse left an open Tooltip's Popper pointed at the now-detached
                                                  // old anchor -> MUI "anchorEl ... invalid" warning on Cancel.
                                                  <Box key="edit-actions" sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                    <Tooltip title="Save">
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          color="primary"
                                                          onClick={handleSaveItem}
                                                          disabled={savingItem}
                                                        >
                                                          <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                      <span>
                                                        <IconButton
                                                          size="small"
                                                          onClick={handleCancelEdit}
                                                          disabled={savingItem}
                                                        >
                                                          <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                      </span>
                                                    </Tooltip>
                                                  </Box>
                                                ) : (
                                                  <Box key="row-actions" sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                    <Tooltip title="Edit quantity">
                                                      <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEditItem(count.id, item)}
                                                      >
                                                        <EditIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete SKU">
                                                      <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteItem(count.id, item.sku)}
                                                      >
                                                        <DeleteIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Box>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            {/* Show batch details for multi-batch items */}
                                            {hasMultipleBatches && item.batches.map((batch, bIdx) => {
                                              // Find edited batch quantity if editing
                                              const editedBatch = isEditingMultiBatch
                                                ? editingItem.batches.find(b => b.batch_number === batch.batch_number)
                                                : null;

                                              return (
                                                <TableRow key={`${idx}-batch-${bIdx}`} sx={{ backgroundColor: 'grey.100' }}>
                                                  <TableCell sx={{ pl: 4 }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                      └ {batch.batch_number || 'Unknown'}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="caption" color="textSecondary">—</Typography>
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    {isEditingMultiBatch ? (
                                                      <TextField
                                                        size="small"
                                                        type="number"
                                                        value={editedBatch?.counted_quantity || 0}
                                                        onChange={(e) => handleBatchQuantityChange(batch.batch_number, e.target.value)}
                                                        sx={{ width: 80 }}
                                                        inputProps={{ style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                                                      />
                                                    ) : (
                                                      <Typography variant="caption">{formatNumber(batch.counted_quantity || 0)}</Typography>
                                                    )}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="caption" color="textSecondary">—</Typography>
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    {batch.expiry_date && (
                                                      <Typography variant="caption" color="textSecondary">
                                                        Exp: {formatDate(batch.expiry_date)}
                                                      </Typography>
                                                    )}
                                                  </TableCell>
                                                  <TableCell></TableCell>
                                                  <TableCell></TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </React.Fragment>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                                    No items found for this count
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Delete confirmation dialog */}
      <DeleteCountsDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        counts={selectedCounts}
        loading={deleting}
        error={error}
      />
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}

export default PhysicalCountHistory;
